import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Select, Space, Table, Tag, Typography, Upload, message } from "antd";
import { SearchOutlined, UploadOutlined } from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useThemeStyles } from "../context/ThemeContext";
import * as API from "../services/api";
import { formatReadableProjectId } from "../utils/helpers";
import { formatFileUploadSuccess } from "../utils/successMessages";
import { formatProjectAdminNames } from "../components/ProjectDirectoryViews";
import PageHeader from "../components/PageHeader";
import { pageShellStyle } from "../theme";

const formatBytes = (bytes) => {
  if (typeof bytes !== "number" || Number.isNaN(bytes)) return "0 B";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

const getDisplayFileName = (path) => {
  if (!path) return "-";
  const segments = path.split("/").filter(Boolean);
  return segments.length ? segments[segments.length - 1] : path;
};

const PAGE_SIZE = 10;

const S3Files = () => {
  const { token, isSuperAdmin } = useAuth();
  const themeStyles = useThemeStyles();

  /** Readable inline paths/buckets (antd `code` inside `type="secondary"` was nearly invisible in light mode). */
  const codeChipStyle = useMemo(
    () => ({
      padding: "2px 8px",
      borderRadius: 6,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: 13,
      lineHeight: "20px",
      backgroundColor: themeStyles.elevatedBg,
      color: themeStyles.textPrimary,
      border: `1px solid ${themeStyles.borderColor}`,
    }),
    [themeStyles],
  );
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bucketOptions, setBucketOptions] = useState([]);
  const [bucketsLoading, setBucketsLoading] = useState(false);
  const [bucketsReady, setBucketsReady] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [folderFilter, setFolderFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ sort_by: "last_modified", order: "desc" });
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [folderCounts, setFolderCounts] = useState({});
  const [showPermissionCaution, setShowPermissionCaution] = useState(false);
  const [rbacByProjectId, setRbacByProjectId] = useState({});
  const filesRequestRef = useRef(0);

  const selectedBucketEntry = useMemo(
    () => bucketOptions.find((b) => b.project_id === selectedProjectId),
    [bucketOptions, selectedProjectId],
  );
  const selectedBucket = selectedBucketEntry?.bucket || null;

  const isPermissionError = (detail) => {
    const msg = String(detail || "").toLowerCase();
    return (
      msg.includes("accessdenied") ||
      msg.includes("not authorized") ||
      msg.includes("not authorized to perform") ||
      msg.includes("s3:putobject") ||
      msg.includes("s3:getobject") ||
      msg.includes("s3:listbucket") ||
      msg.includes("s3:deleteobject")
    );
  };

  const loadBuckets = useCallback(async () => {
    if (!token) return;
    setBucketsLoading(true);
    try {
      const response = await API.listProjectS3Buckets(token);
      const list = response?.data?.buckets || [];
      setBucketOptions(list);
      setSelectedProjectId((prev) => {
        if (prev && list.some((b) => b.project_id === prev)) return prev;
        return list.length ? list[0].project_id : null;
      });
    } catch (error) {
      message.error(error?.response?.data?.detail || "Failed to load project S3 buckets");
      setBucketOptions([]);
    } finally {
      setBucketsLoading(false);
      setBucketsReady(true);
    }
  }, [token]);

  const loadFiles = useCallback(async () => {
    if (!token || !bucketsReady) return;
    if (!selectedProjectId) {
      setFiles([]);
      setTotal(0);
      setTotalAll(0);
      setFolderCounts({});
      return;
    }
    const requestId = ++filesRequestRef.current;
    setLoading(true);
    try {
      const response = await API.listProjectS3Files(token, selectedProjectId, {
        folder: folderFilter === "all" ? undefined : folderFilter,
        search,
        sort_by: sort.sort_by,
        order: sort.order,
        page,
        page_size: PAGE_SIZE,
      });
      if (requestId !== filesRequestRef.current) return;
      const data = response?.data || {};
      setFiles(data.files || []);
      setTotal(data.total || 0);
      setTotalAll(data.total_all || 0);
      setFolderCounts(data.folder_counts || {});
      // Bucket is readable/listable for current principal.
      setShowPermissionCaution(false);
    } catch (error) {
      if (requestId !== filesRequestRef.current) return;
      const detail = error?.response?.data?.detail || "Failed to load S3 files";
      if (isPermissionError(detail)) {
        setShowPermissionCaution(true);
      } else {
        setShowPermissionCaution(false);
      }
      message.error(detail);
    } finally {
      if (requestId === filesRequestRef.current) setLoading(false);
    }
  }, [token, selectedProjectId, bucketsReady, folderFilter, search, sort, page]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      setSearch((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const selectProject = (projectId) => {
    setSelectedProjectId(projectId);
    setFolderFilter("all");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const selectFolder = (folder) => {
    setFolderFilter(folder);
    setPage(1);
  };

  useEffect(() => {
    loadBuckets();
  }, [loadBuckets]);

  useEffect(() => {
    if (!token || !isSuperAdmin) {
      setRbacByProjectId({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await API.listRbacProjects(token);
        if (cancelled) return;
        const map = {};
        for (const row of Array.isArray(response?.data) ? response.data : []) {
          if (row?.project_id) map[row.project_id] = row;
        }
        setRbacByProjectId(map);
      } catch {
        if (!cancelled) setRbacByProjectId({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, isSuperAdmin]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    const verifyBucketPermissions = async () => {
      if (!token || !bucketsReady || !selectedBucket) {
        setShowPermissionCaution(false);
        return;
      }
      try {
        const response = await API.checkS3BucketPermissions(token, selectedBucket);
        const hasAllPermissions = Boolean(response?.data?.all_required_granted);
        setShowPermissionCaution(!hasAllPermissions);
      } catch (error) {
        const detail = error?.response?.data?.detail || "Failed to check S3 permissions";
        setShowPermissionCaution(isPermissionError(detail));
      }
    };
    verifyBucketPermissions();
  }, [token, bucketsReady, selectedBucket]);

  const handleUpload = async ({ file, onSuccess, onError }) => {
    if (!selectedBucket && bucketOptions.length > 0) {
      message.warning("Select a project first");
      onError?.(new Error("No bucket"));
      return;
    }
    try {
      setUploading(true);
      await API.uploadS3File(token, file, selectedBucket || undefined);
      message.success(formatFileUploadSuccess(file.name));
      setShowPermissionCaution(false);
      onSuccess?.("ok");
      loadFiles();
    } catch (error) {
      const detail = error?.response?.data?.detail || "Upload failed";
      if (isPermissionError(detail)) {
        setShowPermissionCaution(true);
      }
      message.error(detail);
      onError?.(new Error(detail));
    } finally {
      setUploading(false);
    }
  };

  const sortOrderFor = (key) =>
    sort.sort_by === key ? (sort.order === "asc" ? "ascend" : "descend") : null;

  const columns = [
    {
      title: "File Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      width: "40%",
      sorter: true,
      sortOrder: sortOrderFor("name"),
      render: (value, record) => (
        <Typography.Text style={{ color: themeStyles.textPrimary }} title={record.key}>
          {getDisplayFileName(value || record.key)}
        </Typography.Text>
      ),
    },
    {
      title: "Folder",
      dataIndex: "folder",
      key: "folder",
      width: "12%",
      render: (value) => <Tag>{value || "Other"}</Tag>,
    },
    {
      title: "File Size",
      dataIndex: "size",
      key: "size",
      width: "12%",
      showSorterTooltip: { title: "Sort by file size" },
      sorter: true,
      sortOrder: sortOrderFor("size"),
      render: (value) => formatBytes(value),
    },
    {
      title: "Last Modified",
      dataIndex: "last_modified",
      key: "last_modified",
      width: "22%",
      sortDirections: ["descend", "ascend"],
      showSorterTooltip: { title: "Sort by last modified time" },
      sorter: true,
      sortOrder: sortOrderFor("last_modified"),
      onCell: () => ({
        style: { paddingRight: 16 },
      }),
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
  ];

  const uploadDisabled = bucketOptions.length > 0 && !selectedBucket;

  const handleTableChange = (pagination, _filters, sorter) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextSort = s?.order
      ? { sort_by: s.columnKey, order: s.order === "ascend" ? "asc" : "desc" }
      : { sort_by: "last_modified", order: "desc" };
    if (nextSort.sort_by !== sort.sort_by || nextSort.order !== sort.order) {
      setSort(nextSort);
      setPage(1);
      return;
    }
    setPage(pagination.current || 1);
  };

  const projectNameCounts = useMemo(() => {
    const m = {};
    for (const b of bucketOptions) {
      const n = b.project_name || formatReadableProjectId(b.project_id) || "Project";
      m[n] = (m[n] || 0) + 1;
    }
    return m;
  }, [bucketOptions]);

  return (
    <div style={pageShellStyle}>
      <PageHeader
        title="Document Library"
        // actions={(
        //   <Upload customRequest={handleUpload} showUploadList={false} disabled={uploadDisabled}>
        //     <Button
        //       type={themeStyles.isLight ? 'default' : 'primary'}
        //       className={themeStyles.isLight ? 'light-theme-cta-upload' : undefined}
        //       icon={<UploadOutlined />}
        //       loading={uploading}
        //       disabled={uploadDisabled}
        //     >
        //       Upload File
        //     </Button>
        //   </Upload>
        // )}
      />
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 16,
          gap: 12,
        }}
      >
        <Space wrap size="middle" align="center">
          <Typography.Text type="secondary">Project name</Typography.Text>
          <Select
            style={{ minWidth: 220 }}
            placeholder={bucketOptions.length ? "Select project" : "No buckets configured on projects"}
            loading={bucketsLoading}
            value={selectedProjectId}
            onChange={selectProject}
            showSearch
            filterOption={(input, option) => {
              const b = bucketOptions.find((x) => x.project_id === option?.value);
              const label =
                b?.project_name || (b?.project_id != null ? formatReadableProjectId(b.project_id) : "") || "Project";
              return label.toLowerCase().includes(String(input ?? "").toLowerCase());
            }}
            options={bucketOptions.map((b) => {
              const projectLabel =
                b.project_name || formatReadableProjectId(b.project_id) || "Project";
              const dup =
                (projectNameCounts[projectLabel] || 0) > 1;
              return {
                value: b.project_id,
                label: (
                  <span title={dup ? `S3 bucket: ${b.bucket}` : undefined}>
                    {projectLabel}
                  </span>
                ),
              };
            })}
            disabled={!bucketOptions.length}
          />
          {selectedBucket && selectedBucketEntry ? (
            <Space size={8} align="center" wrap>
              <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                S3 bucket
              </Typography.Text>
              <Typography.Text style={codeChipStyle} component="span">
                {selectedBucketEntry.bucket}
              </Typography.Text>
              {isSuperAdmin && selectedBucketEntry.project_id ? (
                <>
                  <Typography.Text type="secondary" style={{ whiteSpace: "nowrap" }}>
                    Admin
                  </Typography.Text>
                  <Typography.Text style={{ color: themeStyles.textPrimary }}>
                    {formatProjectAdminNames(rbacByProjectId[selectedBucketEntry.project_id])}
                  </Typography.Text>
                </>
              ) : null}
            </Space>
          ) : null}
        </Space>
      </div>
      {selectedProjectId ? (
        <Space wrap size="middle" align="center" style={{ marginBottom: 12 }}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: themeStyles.textTertiary }} />}
            placeholder="Search files"
            aria-label="Search files"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ width: 260 }}
          />
          <Select
            style={{ minWidth: 190 }}
            value={folderFilter}
            onChange={selectFolder}
            aria-label="Filter by folder"
            options={[
              { value: "all", label: `All folders (${totalAll.toLocaleString()})` },
              ...Object.keys(folderCounts)
                .sort()
                .map((f) => ({ value: f, label: `${f} (${folderCounts[f].toLocaleString()})` })),
            ]}
          />
        </Space>
      ) : null}

      {!bucketOptions.length && !bucketsLoading ? (
        <Typography.Paragraph type="warning">
          No S3 buckets are configured on any project. Add a bucket in project settings (create or edit a project), verify it, then refresh this page.
        </Typography.Paragraph>
      ) : null}
      {showPermissionCaution ? (
        <Typography.Paragraph
          type="warning"
          style={{ marginTop: 0 }}
        >
          Caution: Your AWS role/user must have both read and write access on the selected bucket to avoid upload errors.
          Required permissions include{" "}
          <Typography.Text style={codeChipStyle} component="span">
            s3:PutObject
          </Typography.Text>
          ,{" "}
          <Typography.Text style={codeChipStyle} component="span">
            s3:GetObject
          </Typography.Text>
          ,{" "}
          <Typography.Text style={codeChipStyle} component="span">
            s3:ListBucket
          </Typography.Text>
          , and{" "}
          <Typography.Text style={codeChipStyle} component="span">
            s3:DeleteObject
          </Typography.Text>{" "}
          (for upload and file management).
        </Typography.Paragraph>
      ) : null}

      <Table
        rowKey={(record) => record.key}
        columns={columns}
        dataSource={files}
        loading={loading}
        size="small"
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (n) => `${n.toLocaleString()} files`,
        }}
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default S3Files;
